<template>
  <div>
    <div class="report-actions d-flex justify-content-end">
      <div class="text-right">
        <eg-b-button
          size="sm"
          variant="outline-info"
          class="mr-3"
          @click="editorShow = !editorShow"
        >
          {{ editorShow ? `Save ${type} report` : `Edit ${type} report` }}
        </eg-b-button>
        <eg-b-button
          size="sm"
          variant="outline-info"
          class=""
          @click="copyData({ data: content, type })"
        >
          Copy {{ type }}
        </eg-b-button>
      </div>
    </div>
    <essay-report-editor
      v-if="editorShow"
      class="feedback-box"
      :content="content"
      @updated="(newHtml) => updateContent(type, newHtml)"
    />
    <div v-else class="feedback-box content" v-html="content"></div>
  </div>
</template>

<script>
import { updateEssay } from "@/api";
import strIsHTML from "@/utils/strIsHTML";
import EssayReportEditor from "@/components/essay/essay_report_editor.vue";

const trimHTML = (html) => {
  return html.replaceAll("\n", "");
};

export default {
  components: {
    EssayReportEditor,
  },

  props: {
    type: {
      type: String,
      required: true,
      validator: (value) => ["feedback", "errors"].includes(value),
    },

    essay: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      editorShow: false,
    };
  },

  computed: {
    content() {
      return strIsHTML(this.essay[this.type])
        ? trimHTML(this.essay[this.type])
        : this.essay[this.type];
    },

    isHTML() {
      const htmlDocument = new DOMParser().parseFromString(
        // NOTE: don't remember why error, but it's old version, just saved as before
        this.essay.errors,
        "text/html"
      );

      return Array.from(htmlDocument.body.childNodes).some(
        (node) => node.nodeType === 1
      );
    },
  },

  methods: {
    async updateContent(type = "feedback", newHtml) {
      this.$emit("updated", { html: newHtml, type });
      try {
        await updateEssay(this.essay.id, {
          [type]: newHtml,
        });
        this.$showToastSuccess(
          `The essay ${type} section was successfully saved.`,
          { title: "Success" }
        );
      } catch (error) {
        this.editorShow = true;
        this.$showToastError(
          `There was a problem saving the ${type} section. Please try again.`
        );
      }
    },

    async copyData({ data, type }) {
      try {
        let richText = data;
        let plainText = data;

        if (this.isHTML) {
          let div = document.createElement("div");
          div.innerHTML = data;
          richText = div.innerHTML;
          plainText = div.innerText || "";
          div = null;
        }

        await this.copyRichText(richText, plainText);

        this.$showToastSuccess(
          `The essay ${type} section was copied successfully.`,
          { title: "Copy", autoHideDelay: 3000 }
        );
      } catch {
        this.$showToastError(
          `There was an error copying the essay ${type} section. Please try again.`
        );
      }
    },
    async copyRichText(rich, plain) {
      if (typeof ClipboardItem !== "undefined") {
        // Shiny new Clipboard API, not fully supported in some browsers Firefox.
        // https://developer.mozilla.org/en-US/docs/Web/API/Clipboard_API#browser_compatibility
        const html = new Blob([rich], { type: "text/html" });
        const text = new Blob([plain], { type: "text/plain" });
        const data = new ClipboardItem({
          "text/html": html,
          "text/plain": text,
        });
        await navigator.clipboard.write([data]);
      } else {
        // Fallback using the deprecated `document.execCommand`.
        // https://developer.mozilla.org/en-US/docs/Web/API/Document/execCommand#browser_compatibility
        const cb = (e) => {
          e.clipboardData.setData("text/html", rich);
          e.clipboardData.setData("text/plain", plain);
          e.preventDefault();
        };
        document.addEventListener("copy", cb);
        document.execCommand("copy");
        document.removeEventListener("copy", cb);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.report-actions {
  position: absolute;
  right: 25px;
  top: 48px;
}

.feedback-box {
  white-space: pre-wrap;
  padding: 25px;
  border-radius: 7px;
  border: 1px solid rgba(0, 0, 0, 0.125);
  height: 100%;

  &.light {
    background: var(--Background-Light, #f8f9fa);
  }

  .error {
    background-color: #fc8080;
    padding: 2px;
  }
}

.feedback-box.content,
.feedback-box.ql-container {
  border-top-left-radius: 0;
  border-top-right-radius: 0;
  border-top: none;
  padding-top: 0px;
}

.feedback-box.content {
  padding-top: 43.5px;
}

.feedback-box::v-deep {
  p,
  h2,
  h3 {
    margin: 0;
  }

  h3:not(:first-child) {
    margin-top: 15px;
  }

  h2:not(:first-child) {
    margin-top: 25px;
    margin-bottom: 25px;
  }

  h3 {
    margin-bottom: 15px;
  }

  .criteria-grade {
    margin-bottom: 5px;
  }

  p {
    margin-bottom: 20px;
  }

  .ip-suggestion,
  .sp-error,
  .g-error,
  .s-error,
  .c-error,
  .cl-error,
  .l-error,
  .g-suggestion,
  .s-suggestion,
  .c-suggestion,
  .cl-suggestion,
  .l-suggestion,
  .g-impact,
  .s-impact,
  .c-impact,
  .cl-impact,
  .l-impact {
    display: block;
    white-space: normal;
    margin-bottom: 0px;
  }

  .ip-suggestion,
  .g-suggestion,
  .s-suggestion,
  .c-suggestion,
  .cl-suggestion,
  .l-suggestion {
    margin-bottom: 20px;
  }

  h2 {
    border-bottom: 1px solid gray;
  }

  .criteria-suggestion {
    border-radius: 4px;
    border: 1px solid #17a2b8;
    background: rgba(23, 162, 184, 0.1);
    padding: 16px;

    strong {
      color: #0d92a7;
      display: flex;
      align-items: center;

      &:before {
        content: "";
        width: 16px;
        height: 16px;
        margin-right: 5px;
        display: inline-block;
        background-image: url("data:image/svg+xml;base64,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");
      }
    }

    .cs-output {
      display: block;
      margin-top: -15px;
    }
  }
}
</style>
